import { useEffect, useMemo, useState } from 'react'

import { useSelfServeExplorerContext } from '../QuoteSelfServeExplorerContext'
import { AllowanceStrategies, type CoverageLevel } from '../types'
import { AllowanceStrategySelector } from './AllowanceStrategySelector'
import { FlatDefaultAllowanceStep } from './FlatDefaultAllowanceStep'
import { SmartCoverageAmountStep } from './SmartCoverageAmountStep'
import { SmartDependentCoverageStep } from './SmartDependentCoverageStep'
import { type Step, StepNavigator } from './StepNavigator'

// Wizard step constants
const WizardSteps = {
  Strategy: 'strategy',
  FlatDefault: 'flat_default',
  SmartCoverage: 'smart_coverage',
  SmartDependent: 'smart_dependent'
} as const

type WizardStep = typeof WizardSteps[keyof typeof WizardSteps]

const FLAT_STEPS: WizardStep[] = [WizardSteps.Strategy, WizardSteps.FlatDefault]
const SMART_STEPS: WizardStep[] = [WizardSteps.Strategy, WizardSteps.SmartCoverage, WizardSteps.SmartDependent]

// Coverage level display labels
const COVERAGE_LEVEL_LABELS: Record<CoverageLevel, string> = {
  lowest_cost: 'Lowest cost',
  best_fit: 'Best fit for you',
  better_benefits: 'Better benefits'
}

interface AllowanceWizardProps {
  onComplete?: () => void
}

export function AllowanceWizard ({ onComplete }: AllowanceWizardProps) {
  const { allowanceStrategy, selectedCoverageLevel, setIsOnFinalWizardStep, setIsUsingWizard } = useSelfServeExplorerContext()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)

  const isFlat = allowanceStrategy === AllowanceStrategies.Flat
  const steps = isFlat ? FLAT_STEPS : SMART_STEPS
  const currentStep = steps[currentStepIndex]

  const isLastStep = currentStepIndex === steps.length - 1

  // Mark wizard as active on mount
  useEffect(() => {
    setIsUsingWizard(true)
    return () => setIsUsingWizard(false)
  }, [setIsUsingWizard])

  // Update context when on final step
  useEffect(() => {
    setIsOnFinalWizardStep(isLastStep)
  }, [isLastStep, setIsOnFinalWizardStep])

  const handleNext = () => {
    if (isLastStep) {
      onComplete?.()
    } else {
      setCurrentStepIndex(prev => Math.min(prev + 1, steps.length - 1))
    }
  }

  const handleBack = () => {
    setCurrentStepIndex(prev => Math.max(prev - 1, 0))
  }

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex !== currentStepIndex && stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStepIndex(stepIndex)
    }
  }

  // Reset to first step when strategy changes
  const handleStrategyChange = () => {
    setCurrentStepIndex(0)
  }

  const navigationSteps: Step[] = useMemo(() => {
    if (isFlat) {
      return [
        {
          id: WizardSteps.Strategy,
          label: 'Allowance strategy',
          value: 'Same amount for everyone',
          placeholder: 'Set allowance strategy'
        },
        {
          id: WizardSteps.FlatDefault,
          label: 'Default allowance',
          placeholder: 'Set default allowance'
        }
      ]
    }

    return [
      {
        id: WizardSteps.Strategy,
        label: 'Allowance strategy',
        value: 'Smart allowances',
        placeholder: 'Set allowance strategy'
      },
      {
        id: WizardSteps.SmartCoverage,
        label: 'Coverage amount',
        value: currentStepIndex > 1 ? COVERAGE_LEVEL_LABELS[selectedCoverageLevel] : undefined,
        placeholder: 'Set coverage amount'
      },
      {
        id: WizardSteps.SmartDependent,
        label: 'Dependent coverage',
        placeholder: 'Set dependent coverage'
      }
    ]
  }, [isFlat, currentStepIndex, selectedCoverageLevel])

  const renderStepContent = () => {
    switch (currentStep) {
      case WizardSteps.Strategy:
        return (
          <AllowanceStrategySelector
            onNext={handleNext}
            onStrategyChange={handleStrategyChange}
          />
        )
      case WizardSteps.FlatDefault:
        return (
          <FlatDefaultAllowanceStep
            onBack={handleBack}
          />
        )
      case WizardSteps.SmartCoverage:
        return (
          <SmartCoverageAmountStep
            onBack={handleBack}
            onNext={handleNext}
          />
        )
      case WizardSteps.SmartDependent:
        return (
          <SmartDependentCoverageStep
            onBack={handleBack}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className='flex flex-col'>
      <StepNavigator
        steps={navigationSteps}
        currentStepIndex={currentStepIndex}
        onStepClick={handleStepClick}
      />
      {renderStepContent()}
    </div>
  )
}
